import { useCallback, useContext } from 'react';
import { PathnameContext, HistoryContext, SearchParamsContext, HistoryBasePathContext } from './context';
import { useSyncExternalStoreWithSelector } from 'use-sync-external-store/with-selector';

/**
 * Hook to access the current pathname from the routing context.
 *
 * @returns The current pathname as a string.
 */
export function usePathname() {
  const pathname = useContext(PathnameContext);
  if (pathname === null) {
    throw new Error('usePathname must be used in RouterProvider');
  }
  return pathname;
}

/**
 * Hook to access the history object from the routing context.
 *
 * Note: Unlike react-router-dom's useHistory, this hook does NOT trigger
 * a rerender when the location changes.
 *
 * @returns The history object from the routing context.
 */
export function useHistory() {
  const history = useContext(HistoryContext);
  if (history === null) {
    throw new Error('useHistory must be used in RouterProvider');
  }
  return history;
}

/**
 * Hook to access the current URL search parameters from the routing context.
 *
 * @returns The search parameters as a URLSearchParams object.
 */
export function useSearchParams() {
  const searchParams = useContext(SearchParamsContext);
  if (searchParams === null) {
    throw new Error('useSearchParams must be used in RouterProvider');
  }
  return searchParams;
}

/**
 * Hook to access the history base path extracted from the URL.
 *
 * Returns the resolved base pathname and any parameters extracted from
 * `historyBasename` patterns like `/:lang/marketplace`.
 *
 * For example, if the URL is `/it/marketplace/apps` and the basename is `/:lang/marketplace`,
 * this hook returns `{ basePathname: '/it/marketplace', params: { lang: 'it' } }`.
 */
export function useHistoryBasePath() {
  const basePath = useContext(HistoryBasePathContext);
  if (basePath === null) {
    throw new Error('useHistoryBasePath must be used in RouterProvider');
  }
  return basePath;
}
/**
 * Hook to access a specific value from the location object and subscribe to changes.
 *
 * @param selector A function that takes the location object and selects a value.
 * @returns The selected value.
 */
export function useLocationWithSelector(selector, isEqual) {
  const history = useHistory();
  const getLocation = useCallback(() => {
    return history.location;
  }, [history]);
  return useSyncExternalStoreWithSelector(history.listen, getLocation, null, selector, isEqual);
}